import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, FileText, Star, Loader2, X, Download, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CommunityShare({ courseId, courseName, specialization, userName, userEmail }) {
  const [shares, setShares] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [name, setName] = useState(userName || '');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    loadShares();
  }, [courseId]);

  const loadShares = async () => {
    try {
      const res = await axios.get(`${API}/community/shares`, { params: { course_id: courseId } });
      setShares(res.data);
    } catch (e) {}
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !name.trim()) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('course_id', courseId);
      formData.append('specialization', specialization || '');
      formData.append('contributor_name', name.trim());
      formData.append('contributor_email', userEmail || '');
      formData.append('description', description.trim());
      
      await axios.post(`${API}/community/share`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccessMsg('תודה רבה על השיתוף! החומר נוסף בהצלחה');
      setFile(null);
      setDescription('');
      setShowUpload(false);
      loadShares();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      alert(err.response?.data?.detail || 'שגיאה בהעלאה');
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return '📄';
    if (type?.includes('word') || type?.includes('document')) return '📝';
    if (type?.includes('image')) return '🖼️';
    return '📎';
  };

  return (
    <div className="bg-white p-6 sm:p-8 rounded-3xl shadow-lg border" data-testid="community-share-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <Star className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="text-xl font-black text-slate-900">תתרום לנו סיכומים ומבחנים</h3>
            <p className="text-slate-400 text-xs">{shares.length} חומרים שותפו</p>
          </div>
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all active:scale-95"
          data-testid="share-material-btn"
        >
          <Upload className="w-4 h-4" />
          שתף חומר
        </button>
      </div>

      {/* Success Message */}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-4 flex items-center gap-2 text-sm font-bold" data-testid="share-success">
          <Star className="w-4 h-4 text-amber-500" />
          {successMsg}
        </div>
      )}

      {/* Upload Form */}
      {showUpload && (
        <form onSubmit={handleUpload} className="mb-6 bg-amber-50 p-5 rounded-2xl border-2 border-dashed border-amber-300" data-testid="share-upload-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-slate-900">שיתוף חומר לקורס {courseName}</h4>
            <button type="button" onClick={() => setShowUpload(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">שם שלך</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-amber-300 outline-none"
                placeholder="השם שלך"
                required
                data-testid="share-name-input"
              />
            </div>
            
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">בחר קובץ (PDF, Word, תמונה)</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.txt"
                onChange={(e) => setFile(e.target.files[0])}
                className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-amber-500 file:text-white hover:file:bg-amber-600 cursor-pointer"
                required
                data-testid="share-file-input"
              />
            </div>
            
            <div>
              <label className="text-xs font-bold text-slate-500 block mb-1">תיאור (אופציונלי)</label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-4 py-2.5 border rounded-xl text-sm focus:ring-2 focus:ring-amber-300 outline-none"
                placeholder="לדוגמה: סיכום למבחן, שאלות תרגול..."
                data-testid="share-description-input"
              />
            </div>
            
            <button
              type="submit"
              disabled={!file || !name.trim() || uploading}
              className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-95"
              data-testid="share-submit-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'מעלה...' : 'שתף עכשיו'}
            </button>
          </div>
        </form>
      )}

      {/* Shared Materials List */}
      {shares.length > 0 ? (
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {shares.map((share) => (
            <div key={share.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors group" data-testid={`share-item-${share.id}`}>
              <span className="text-2xl flex-shrink-0">{getFileIcon(share.file_type)}</span>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900 text-sm truncate">{share.filename}</p>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-amber-400" />
                    {share.contributor_name}
                  </span>
                  <span>{formatSize(share.file_size)}</span>
                  <span>{formatDate(share.created_at)}</span>
                </div>
                {share.description && (
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{share.description}</p>
                )}
              </div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${share.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 w-8 h-8 bg-teal-100 hover:bg-teal-200 rounded-lg flex items-center justify-center transition-colors"
                data-testid={`download-share-${share.id}`}
              >
                <Download className="w-4 h-4 text-teal-700" />
              </a>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-400">
          <Upload className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm font-medium">עדיין אין חומרים משותפים לקורס הזה</p>
          <p className="text-xs">היה הראשון לשתף!</p>
        </div>
      )}
    </div>
  );
}

export default CommunityShare;
